import React, { useState, useEffect, useRef } from 'react';
import type { InventoryItem, BulkPurchase, BulkPurchaseBag } from '../../types';
import XCircleIcon from '../icons/XCircleIcon';
import PlusIcon from '../icons/PlusIcon';
import TrashIcon from '../icons/TrashIcon';

interface BulkPurchaseModalProps {
  item: InventoryItem;
  bulkPurchase: BulkPurchase | null;
  onClose: () => void;
  onSave: (bulkPurchase: BulkPurchase) => void;
  onDelete?: (bulkPurchaseId: string) => void;
}

const BulkPurchaseModal: React.FC<BulkPurchaseModalProps> = ({ item, bulkPurchase, onClose, onSave, onDelete }) => {
  const [formData, setFormData] = useState({
    purchaseDate: new Date().toISOString().split('T')[0],
    totalQuantity: 0,
    weeklyAllocation: 0,
    notes: '',
    useBags: false,
    numberOfBags: 1,
    quantityPerBag: 0,
  });
  const [bags, setBags] = useState<BulkPurchaseBag[]>([]);

  // Use a ref to track if we've initialized to prevent unnecessary resets
  const initializedRef = useRef<string | null>(null);
  
  useEffect(() => {
    const bulkPurchaseId = bulkPurchase?.id || 'new';
    
    // Only reset form if we're opening a different bulk purchase
    if (initializedRef.current === bulkPurchaseId) {
      return; // Already initialized for this bulk purchase
    }
    
    initializedRef.current = bulkPurchaseId;
    
    if (bulkPurchase) {
      const hasBags = bulkPurchase.bags && bulkPurchase.bags.length > 0;
      setFormData({
        purchaseDate: new Date(bulkPurchase.purchaseDate).toISOString().split('T')[0],
        totalQuantity: bulkPurchase.totalQuantity,
        weeklyAllocation: bulkPurchase.weeklyAllocation,
        notes: bulkPurchase.notes || '',
        useBags: hasBags,
        numberOfBags: hasBags ? bulkPurchase.bags!.length : 1,
        quantityPerBag: hasBags && bulkPurchase.bags!.length > 0 ? bulkPurchase.bags![0].quantity : 0,
      });
      setBags(bulkPurchase.bags || []);
    } else {
      setFormData({
        purchaseDate: new Date().toISOString().split('T')[0],
        totalQuantity: 0,
        weeklyAllocation: 0,
        notes: '',
        useBags: false,
        numberOfBags: 1,
        quantityPerBag: 0,
      });
      setBags([]);
    }
  }, [bulkPurchase?.id]); // Only reset when bulkPurchase ID changes

  // Auto-generate bags when number of bags or quantity per bag changes
  useEffect(() => {
    if (formData.useBags && formData.numberOfBags > 0 && formData.quantityPerBag > 0) {
      const currentBagCount = bags.length;
      const targetBagCount = typeof formData.numberOfBags === 'number' ? formData.numberOfBags : parseInt(String(formData.numberOfBags)) || 0;
      const targetQuantityPerBag = typeof formData.quantityPerBag === 'number' ? formData.quantityPerBag : parseFloat(String(formData.quantityPerBag)) || 0;
      
      // Only regenerate if the number of bags changed or if bags array is empty
      if (currentBagCount !== targetBagCount || bags.length === 0) {
        const newBags: BulkPurchaseBag[] = [];
        for (let i = 0; i < targetBagCount; i++) {
          // Try to preserve existing bag data if possible (when editing)
          const existingBag = bags[i];
          // If editing and bag exists, preserve remaining; otherwise, new bag = full quantity
          const shouldPreserve = existingBag && existingBag.id && bulkPurchase;
          newBags.push({
            id: existingBag?.id || `bag-${Date.now()}-${i}`,
            quantity: targetQuantityPerBag,
            remainingQuantity: shouldPreserve ? existingBag.remainingQuantity : targetQuantityPerBag, // New bags start full
            label: existingBag?.label || `Bag ${i + 1}`,
          });
        }
        setBags(newBags);
      } else if (targetQuantityPerBag > 0) {
        // Update quantities if quantity per bag changed but bag count is same
        // Only update if bags don't have remaining quantity set (new bags)
        setBags(bags.map((bag) => {
          // If remaining equals old quantity, it's a new bag - update both
          // Otherwise, preserve remaining quantity (editing existing bag)
          const wasNewBag = bag.remainingQuantity === bag.quantity;
          return {
            ...bag,
            quantity: targetQuantityPerBag,
            remainingQuantity: wasNewBag ? targetQuantityPerBag : Math.min(bag.remainingQuantity, targetQuantityPerBag),
          };
        }));
      }
    } else if (!formData.useBags) {
      setBags([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.useBags, formData.numberOfBags, formData.quantityPerBag, bulkPurchase]);

  const handleAddBag = () => {
    const newBag: BulkPurchaseBag = {
      id: `bag-${Date.now()}-${Math.random()}`,
      quantity: formData.quantityPerBag || 0,
      remainingQuantity: formData.quantityPerBag || 0,
      label: `Bag ${bags.length + 1}`,
    };
    setBags([...bags, newBag]);
    setFormData(prev => ({ ...prev, numberOfBags: prev.numberOfBags + 1 }));
  };

  const handleRemoveBag = (bagId: string) => {
    setBags(bags.filter(bag => bag.id !== bagId));
    setFormData(prev => ({ ...prev, numberOfBags: Math.max(1, prev.numberOfBags - 1) }));
  };

  const handleBagChange = (bagId: string, field: keyof BulkPurchaseBag, value: string | number) => {
    setBags(bags.map(bag => 
      bag.id === bagId 
        ? { ...bag, [field]: value, remainingQuantity: field === 'quantity' ? (value as number) : bag.remainingQuantity }
        : bag
    ));
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: (name === 'totalQuantity' || name === 'weeklyAllocation' || name === 'numberOfBags' || name === 'quantityPerBag') 
        ? (value === '' ? '' : (isNaN(parseFloat(value)) ? 0 : parseFloat(value))) 
        : value,
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    // If using bags, calculate total from bags
    let calculatedTotal = formData.totalQuantity;
    if (formData.useBags && bags.length > 0) {
      calculatedTotal = bags.reduce((sum, bag) => sum + bag.quantity, 0);
      if (calculatedTotal <= 0) {
        alert('Please add at least one bag with a quantity greater than 0');
        return;
      }
      // Validate all bags have quantities
      const invalidBags = bags.filter(bag => bag.quantity <= 0);
      if (invalidBags.length > 0) {
        alert('All bags must have a quantity greater than 0');
        return;
      }
    } else {
      if (formData.totalQuantity <= 0) {
        alert('Total quantity must be greater than 0');
        return;
      }
    }
    
    if (formData.weeklyAllocation <= 0) {
      alert('Weekly allocation must be greater than 0');
      return;
    }
    if (formData.weeklyAllocation > calculatedTotal) {
      alert('Weekly allocation cannot exceed total quantity');
      return;
    }

    const purchaseDate = new Date(formData.purchaseDate).getTime();
    
    // Calculate remaining quantity: if editing, preserve existing remaining; if new, use total
    let remainingQuantity: number;
    if (bulkPurchase) {
      // Editing: preserve existing remaining quantity
      remainingQuantity = bulkPurchase.remainingQuantity;
    } else {
      // New purchase: remaining = total (nothing allocated yet)
      remainingQuantity = formData.useBags && bags.length > 0
        ? bags.reduce((sum, bag) => sum + bag.remainingQuantity, 0)
        : calculatedTotal;
    }

    const newBulkPurchase: BulkPurchase = {
      id: bulkPurchase?.id || `bulk-${Date.now()}`,
      inventoryItemId: item.id,
      purchaseDate,
      totalQuantity: calculatedTotal,
      remainingQuantity,
      weeklyAllocation: formData.weeklyAllocation,
    };

    // Only add optional fields if they have values
    if (formData.useBags && bags.length > 0) {
      newBulkPurchase.bags = bags.map(bag => ({
        id: bag.id,
        quantity: bag.quantity,
        remainingQuantity: bag.remainingQuantity || bag.quantity,
        label: bag.label || undefined,
      }));
    }
    
    if (formData.notes && formData.notes.trim()) {
      newBulkPurchase.notes = formData.notes.trim();
    }

    onSave(newBulkPurchase);
  };

  const handleDelete = () => {
    if (bulkPurchase && onDelete && window.confirm('Are you sure you want to delete this bulk purchase record?')) {
      onDelete(bulkPurchase.id);
      onClose();
    }
  };

  const calculatedTotal = formData.useBags && bags.length > 0
    ? bags.reduce((sum, bag) => sum + bag.quantity, 0)
    : formData.totalQuantity;
  
  const weeksRemaining = calculatedTotal > 0 && formData.weeklyAllocation > 0
    ? Math.floor(calculatedTotal / formData.weeklyAllocation)
    : 0;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex justify-center items-center p-4">
      <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold text-gray-800">
            {bulkPurchase ? 'Edit Bulk Purchase' : 'New Bulk Purchase'}
          </h2>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
            <XCircleIcon className="w-6 h-6" />
          </button>
        </div>

        <div className="mb-4 p-3 bg-gray-50 rounded-lg">
          <p className="text-sm font-medium text-gray-700">Item: <span className="font-bold">{item.name}</span></p>
          <p className="text-xs text-gray-500">Unit: {item.unit}</p>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <label htmlFor="purchaseDate" className="block text-sm font-medium text-gray-700 mb-1">
                Purchase Date
              </label>
              <input
                type="date"
                name="purchaseDate"
                id="purchaseDate"
                value={formData.purchaseDate}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm bg-gray-100 text-gray-900 focus:outline-none focus:ring-pink-500 focus:border-pink-500"
                required
              />
            </div>

          </div>

          <div className="mb-4">
            <label className="flex items-center mb-2">
              <input
                type="checkbox"
                checked={formData.useBags}
                onChange={(e) => setFormData(prev => ({ ...prev, useBags: e.target.checked }))}
                className="mr-2"
              />
              <span className="text-sm font-medium text-gray-700">
                Manage as multiple bags/containers (e.g., 10 bags of 50kg, 5 bags of 100kg)
              </span>
            </label>
          </div>

          {formData.useBags ? (
            <div className="mb-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <label htmlFor="numberOfBags" className="block text-sm font-medium text-gray-700 mb-1">
                    Number of Bags/Containers
                  </label>
                  <input
                    type="number"
                    id="numberOfBags"
                    name="numberOfBags"
                    value={formData.numberOfBags || ''}
                    onChange={(e) => {
                      const val = e.target.value === '' ? '' : parseInt(e.target.value) || 1;
                      setFormData(prev => ({ ...prev, numberOfBags: val === '' ? 1 : val }));
                    }}
                    min="1"
                    step="1"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm bg-white text-gray-900 focus:outline-none focus:ring-pink-500 focus:border-pink-500"
                    required
                  />
                  <p className="text-xs text-gray-500 mt-1">How many bags/containers did you purchase?</p>
                </div>
                <div>
                  <label htmlFor="quantityPerBag" className="block text-sm font-medium text-gray-700 mb-1">
                    Quantity per Bag ({item.unit})
                  </label>
                  <input
                    type="number"
                    id="quantityPerBag"
                    name="quantityPerBag"
                    value={formData.quantityPerBag || ''}
                    onChange={(e) => {
                      const val = e.target.value === '' ? '' : parseFloat(e.target.value) || 0;
                      setFormData(prev => ({ ...prev, quantityPerBag: val === '' ? 0 : val }));
                    }}
                    min="0"
                    step="0.01"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm bg-white text-gray-900 focus:outline-none focus:ring-pink-500 focus:border-pink-500"
                    required
                  />
                  <p className="text-xs text-gray-500 mt-1">How much is in each bag/container?</p>
                </div>
              </div>
              
              {bags.length > 0 && (
                <div className="mb-4 p-3 bg-gray-50 rounded-lg border border-gray-200">
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-gray-600">Number of Bags:</span>
                      <span className="ml-2 font-semibold text-gray-900">{bags.length}</span>
                    </div>
                    <div>
                      <span className="text-gray-600">Quantity per Bag:</span>
                      <span className="ml-2 font-semibold text-gray-900">
                        {bags[0]?.quantity.toFixed(2)} {item.unit}
                        {bags.length > 1 && bags.some(b => b.quantity !== bags[0].quantity) && (
                          <span className="text-xs text-gray-500 ml-1">(varies)</span>
                        )}
                      </span>
                    </div>
                    <div>
                      <span className="text-gray-600">Total Quantity:</span>
                      <span className="ml-2 font-semibold text-gray-900">
                        {bags.reduce((sum, bag) => sum + bag.quantity, 0).toFixed(2)} {item.unit}
                      </span>
                    </div>
                    <div>
                      <span className="text-gray-600">Remaining:</span>
                      <span className="ml-2 font-semibold text-pink-600">
                        {bags.reduce((sum, bag) => sum + bag.remainingQuantity, 0).toFixed(2)} {item.unit}
                      </span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="mb-4">
              <label htmlFor="totalQuantity" className="block text-sm font-medium text-gray-700 mb-1">
                Total Quantity Purchased
              </label>
              <input
                type="number"
                name="totalQuantity"
                id="totalQuantity"
                value={formData.totalQuantity || ''}
                onChange={handleChange}
                min="0"
                step="0.01"
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm bg-gray-100 text-gray-900 focus:outline-none focus:ring-pink-500 focus:border-pink-500"
                required={!formData.useBags}
                placeholder="e.g., 1000"
              />
              <p className="text-xs text-gray-500 mt-1">Total amount purchased (e.g., 1000 kg)</p>
            </div>
          )}

          <div className="mb-4">
            <label htmlFor="weeklyAllocation" className="block text-sm font-medium text-gray-700 mb-1">
              Weekly Allocation
            </label>
            <input
              type="number"
              name="weeklyAllocation"
              id="weeklyAllocation"
              value={formData.weeklyAllocation || ''}
              onChange={handleChange}
              min="0"
              step="0.01"
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm bg-gray-100 text-gray-900 focus:outline-none focus:ring-pink-500 focus:border-pink-500"
              required
              placeholder="e.g., 30 or 40"
            />
            <p className="text-xs text-gray-500 mt-1">
              How much to allocate per week for restaurant use (e.g., 30-40 kg per week)
            </p>
            {weeksRemaining > 0 && (
              <p className="text-xs text-pink-600 mt-1 font-semibold">
                Estimated weeks remaining: ~{weeksRemaining} weeks
              </p>
            )}
          </div>

          <div className="mb-4">
            <label htmlFor="notes" className="block text-sm font-medium text-gray-700 mb-1">
              Notes (Optional)
            </label>
            <textarea
              name="notes"
              id="notes"
              value={formData.notes}
              onChange={handleChange}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm bg-gray-100 text-gray-900 focus:outline-none focus:ring-pink-500 focus:border-pink-500"
              placeholder="Additional notes about this purchase..."
            />
          </div>

          {bulkPurchase && (
            <div className="mb-4 p-3 bg-blue-50 rounded-lg">
              <p className="text-sm text-gray-700">
                <span className="font-semibold">Remaining Quantity:</span> {bulkPurchase.remainingQuantity} {item.unit}
              </p>
              <p className="text-xs text-gray-500 mt-1">
                This will be updated automatically as you allocate stock weekly
              </p>
            </div>
          )}

          <div className="flex justify-between items-center">
            <div>
              {bulkPurchase && onDelete && (
                <button
                  type="button"
                  onClick={handleDelete}
                  className="px-4 py-2 bg-red-500 text-white rounded-md hover:bg-red-600"
                >
                  Delete
                </button>
              )}
            </div>
            <div className="flex space-x-4">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-4 py-2 bg-pink-500 text-white rounded-md hover:bg-pink-600"
              >
                {bulkPurchase ? 'Update' : 'Create'} Bulk Purchase
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default BulkPurchaseModal;

